import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Loader2,
  AlertTriangle,
  Clock,
  Receipt,
  FileWarning,
  Banknote,
  TrendingUp,
  ChevronRight,
  Briefcase,
  CalendarClock,
  Sparkles,
  CheckCircle2,
} from "lucide-react";
import GebuevAuditCard from "@/components/advisor/GebuevAuditCard";
import BulkCamtDropZone from "@/components/advisor/BulkCamtDropZone";
import StammdatenChecklistCard from "@/components/advisor/StammdatenChecklistCard";

function fmtMoney(value, currency = "CHF") {
  const n = Number(value || 0);
  return `${currency} ${n.toLocaleString("de-CH", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("de-CH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function relativeAgo(iso) {
  if (!iso) return "noch keine Aktivität";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const diffH = diffMs / (1000 * 60 * 60);
  if (diffH < 1) return "vor wenigen Minuten";
  if (diffH < 24) return `vor ${Math.round(diffH)} Std.`;
  const diffD = diffH / 24;
  if (diffD < 30) return `vor ${Math.round(diffD)} Tagen`;
  return fmtDate(iso);
}

const VAT_LABELS = {
  ok: { text: "MWST i.O.", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  due_soon: { text: "MWST fällig", className: "bg-amber-50 text-amber-700 border-amber-200" },
  overdue: { text: "MWST überfällig", className: "bg-rose-50 text-rose-700 border-rose-200" },
};

function CockpitMetric({ icon: Icon, label, value, tone = "slate", testid }) {
  const tones = {
    slate: "text-slate-600",
    rose: "text-rose-700",
    amber: "text-amber-700",
    emerald: "text-emerald-700",
    sky: "text-sky-700",
  };
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <Icon className={`h-3.5 w-3.5 ${tones[tone]}`} />
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`ml-auto text-sm font-semibold ${tones[tone]}`}>{value}</div>
    </div>
  );
}

function CompanyCard({ item, onOpen }) {
  const isCritical =
    item.overdue_invoices_count > 0 ||
    item.dunning_due_count > 0 ||
    item.vat_status === "overdue";
  const isWarning =
    item.vat_status === "due_soon" ||
    item.unmatched_bank_tx_count > 0 ||
    item.open_receipts_count > 5;
  const accent = isCritical
    ? "border-l-4 border-l-rose-500"
    : isWarning
      ? "border-l-4 border-l-amber-500"
      : "border-l-4 border-l-emerald-500";
  const vatMeta = VAT_LABELS[item.vat_status] || VAT_LABELS.ok;
  const totalOpenTasks =
    item.overdue_invoices_count +
    item.dunning_due_count +
    item.unmatched_bank_tx_count +
    item.open_receipts_count;

  return (
    <Card
      className={`p-5 bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer ${accent}`}
      onClick={() => onOpen(item)}
      data-testid={`cockpit-company-card-${item.company_id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="text-base font-semibold text-slate-900 truncate">
              {item.name}
            </h3>
            <Badge
              variant="outline"
              className="text-[10px] h-5 capitalize"
              data-testid={`cockpit-role-${item.company_id}`}
            >
              {item.role}
            </Badge>
          </div>
          <div className="text-[11px] text-slate-500 flex items-center gap-2">
            <Clock className="h-3 w-3" />
            <span>{relativeAgo(item.last_activity_at)}</span>
            {item.archived && (
              <Badge variant="outline" className="text-[10px] h-5 bg-slate-100 text-slate-500 border-slate-200">
                archiviert
              </Badge>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <Badge variant="outline" className={`text-[10px] h-5 ${vatMeta.className}`}>
            {vatMeta.text}
          </Badge>
          {item.next_vat_deadline && (
            <span className="text-[10px] text-slate-400 inline-flex items-center gap-1">
              <CalendarClock className="h-3 w-3" />
              {fmtDate(item.next_vat_deadline)}
            </span>
          )}
        </div>
      </div>

      <div className="space-y-1.5 mb-4">
        {item.overdue_invoices_count > 0 && (
          <CockpitMetric
            icon={AlertTriangle}
            label="Überfällige Rechnungen"
            value={`${item.overdue_invoices_count} · ${fmtMoney(item.overdue_invoices_amount, item.currency)}`}
            tone="rose"
            testid={`cockpit-metric-overdue-${item.company_id}`}
          />
        )}
        {item.dunning_due_count > 0 && (
          <CockpitMetric
            icon={FileWarning}
            label="Mahnung fällig"
            value={item.dunning_due_count}
            tone="rose"
            testid={`cockpit-metric-dunning-${item.company_id}`}
          />
        )}
        {item.unmatched_bank_tx_count > 0 && (
          <CockpitMetric
            icon={Banknote}
            label="Bank: ungematcht"
            value={item.unmatched_bank_tx_count}
            tone="amber"
            testid={`cockpit-metric-bank-${item.company_id}`}
          />
        )}
        {item.open_receipts_count > 0 && (
          <CockpitMetric
            icon={Receipt}
            label="Belege zu prüfen"
            value={item.open_receipts_count}
            tone={item.open_receipts_count > 5 ? "amber" : "slate"}
            testid={`cockpit-metric-receipts-${item.company_id}`}
          />
        )}
        {totalOpenTasks === 0 && (
          <div
            className="flex items-center gap-2 text-sm text-emerald-700"
            data-testid={`cockpit-metric-clean-${item.company_id}`}
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            <span>Alles sauber – keine offenen Aufgaben.</span>
          </div>
        )}
      </div>

      <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
        <div className="flex items-center gap-2 text-slate-500">
          <TrendingUp className="h-3 w-3" />
          <span>
            Aktueller Monat:{" "}
            <strong
              className={`font-semibold ${
                item.profit_current_month >= 0 ? "text-emerald-700" : "text-rose-700"
              }`}
            >
              {fmtMoney(item.profit_current_month, item.currency)}
            </strong>
          </span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="rounded-full h-7 px-3 text-xs text-slate-700 hover:bg-slate-100"
          onClick={(e) => {
            e.stopPropagation();
            onOpen(item);
          }}
          data-testid={`cockpit-open-button-${item.company_id}`}
        >
          Öffnen <ChevronRight className="h-3 w-3 ml-0.5" />
        </Button>
      </div>
    </Card>
  );
}

export function AdvisorCockpit() {
  const navigate = useNavigate();
  const { me } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all"); // all | critical | warning | clean

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiClient.get("/my/companies/overview");
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Konnte Cockpit nicht laden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleOpen = (item) => {
    localStorage.setItem("demoCompanyId", item.company_id);
    // Reload triggers CompanyContext re-fetch via auth-email dependency
    window.location.href = "/";
  };

  const sorted = useMemo(() => {
    if (!data?.companies) return [];
    return [...data.companies].sort((a, b) => b.priority_score - a.priority_score);
  }, [data]);

  const filtered = useMemo(() => {
    if (filter === "all") return sorted;
    return sorted.filter((c) => {
      const isCritical =
        c.overdue_invoices_count > 0 ||
        c.dunning_due_count > 0 ||
        c.vat_status === "overdue";
      const isWarning =
        c.vat_status === "due_soon" ||
        c.unmatched_bank_tx_count > 0 ||
        c.open_receipts_count > 5;
      const totalOpen =
        c.overdue_invoices_count +
        c.dunning_due_count +
        c.unmatched_bank_tx_count +
        c.open_receipts_count;
      if (filter === "critical") return isCritical;
      if (filter === "warning") return !isCritical && isWarning;
      if (filter === "clean") return totalOpen === 0;
      return true;
    });
  }, [sorted, filter]);

  const counters = useMemo(() => {
    let critical = 0;
    let warning = 0;
    let clean = 0;
    sorted.forEach((c) => {
      const isCritical =
        c.overdue_invoices_count > 0 ||
        c.dunning_due_count > 0 ||
        c.vat_status === "overdue";
      const isWarning =
        c.vat_status === "due_soon" ||
        c.unmatched_bank_tx_count > 0 ||
        c.open_receipts_count > 5;
      const totalOpen =
        c.overdue_invoices_count +
        c.dunning_due_count +
        c.unmatched_bank_tx_count +
        c.open_receipts_count;
      if (isCritical) critical += 1;
      else if (isWarning) warning += 1;
      else if (totalOpen === 0) clean += 1;
    });
    return { critical, warning, clean };
  }, [sorted]);

  const greeting = me?.name
    ? `Guten Tag, ${me.name.split(" ")[0]}.`
    : "Willkommen zurück.";

  return (
    <div className="space-y-6" data-testid="advisor-cockpit-page">
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-xl bg-slate-900 text-slate-50 flex items-center justify-center flex-shrink-0">
          <Briefcase className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900" data-testid="cockpit-greeting">
            {greeting}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Hier sind Ihre {sorted.length} Mandanten — sortiert nach Dringlichkeit.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-full"
          onClick={() => navigate("/portfolio")}
          data-testid="cockpit-portfolio-button"
        >
          Klassische Portfolio-Ansicht
        </Button>
      </div>

      {/* Hero KPI bar */}
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="cockpit-summary-bar">
          <Card className="p-4 border-l-4 border-l-rose-500 bg-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500">Kritisch</div>
                <div className="text-2xl font-semibold text-slate-900 mt-0.5" data-testid="cockpit-critical-count">
                  {counters.critical}
                </div>
                <div className="text-[11px] text-slate-500">Mandanten brauchen Aufmerksamkeit</div>
              </div>
              <AlertTriangle className="h-7 w-7 text-rose-500/70" />
            </div>
          </Card>
          <Card className="p-4 border-l-4 border-l-amber-500 bg-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500">Beobachten</div>
                <div className="text-2xl font-semibold text-slate-900 mt-0.5" data-testid="cockpit-warning-count">
                  {counters.warning}
                </div>
                <div className="text-[11px] text-slate-500">MWST-Frist nähert sich oder Belege auflaufen</div>
              </div>
              <Clock className="h-7 w-7 text-amber-500/70" />
            </div>
          </Card>
          <Card className="p-4 border-l-4 border-l-emerald-500 bg-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500">Sauber</div>
                <div className="text-2xl font-semibold text-slate-900 mt-0.5" data-testid="cockpit-clean-count">
                  {counters.clean}
                </div>
                <div className="text-[11px] text-slate-500">Keine offenen Aufgaben</div>
              </div>
              <Sparkles className="h-7 w-7 text-emerald-500/70" />
            </div>
          </Card>
        </div>
      )}

      {/* Iter78: Konsolidierter GeBüV-Audit für Treuhänder */}
      <GebuevAuditCard />

      {/* Iter116: Stammdaten-Checklist (gelbe Warnung für fehlende Pflichtfelder) */}
      <StammdatenChecklistCard />

      {/* Iter114: Bulk camt.053 Drop-Zone */}
      <BulkCamtDropZone onImported={load} />


      <div className="flex items-center justify-between flex-wrap gap-3">
        <Tabs value={filter} onValueChange={setFilter} data-testid="cockpit-filter-tabs">
          <TabsList>
            <TabsTrigger value="all" data-testid="cockpit-filter-all">
              Alle ({sorted.length})
            </TabsTrigger>
            <TabsTrigger value="critical" data-testid="cockpit-filter-critical">
              Kritisch ({counters.critical})
            </TabsTrigger>
            <TabsTrigger value="warning" data-testid="cockpit-filter-warning">
              Beobachten ({counters.warning})
            </TabsTrigger>
            <TabsTrigger value="clean" data-testid="cockpit-filter-clean">
              Sauber ({counters.clean})
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="rounded-full"
          onClick={load}
          disabled={loading}
          data-testid="cockpit-refresh-button"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
          Aktualisieren
        </Button>
      </div>

      {loading && !data && (
        <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="cockpit-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Lade Mandanten …
        </div>
      )}
      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          {error}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div
          className="rounded-lg border-2 border-dashed border-slate-200 px-6 py-12 text-center"
          data-testid="cockpit-empty-state"
        >
          <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500 mb-2" />
          <h3 className="text-base font-semibold text-slate-900">
            Keine Mandanten in dieser Kategorie.
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            {filter === "critical"
              ? "Großartig – aktuell keine kritischen Fälle."
              : filter === "warning"
                ? "Alles unter Kontrolle."
                : filter === "clean"
                  ? "Mandanten haben offene Aufgaben."
                  : "Sie haben noch keine Mandanten zugeordnet."}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="cockpit-grid">
        {filtered.map((item) => (
          <CompanyCard key={item.company_id} item={item} onOpen={handleOpen} />
        ))}
      </div>
    </div>
  );
}
